import WelcomeLibraryThumbnail from '@assets/welcome-library/optimized/welcome-library-video/welcome-library-video_poster.jpg';
import { AuthenticatedImage } from '@components/AuthenticatedImage/AuthenticatedImage';
import {
  DataTable,
  GridCellParams,
  GridColDef,
  GridRenderCellParams,
  GridRowClassNameParams,
  GridRowHeightParams,
  GridSortModel,
  useGridApiRef,
} from '@components/DataTable';
import { LibraryCardPlaceholder } from '@components/LibraryThumbnail/LibraryCardPlaceholder';
import { useLibraryThumbnail } from '@components/LibraryThumbnail/useLibraryThumbnail';
import { OmniMatchAllToggle, useTableFindShortcut } from '@components/OmniFilter/TableOmniSearch';
import { PinButton } from '@components/PinButton/PinButton';
import { VerbRail } from '@components/VerbRail/VerbRail';
import { useGridColumnPreferences } from '@lib/hooks/useGridColumnPreferences';
import { useLibraryVerbs } from '@lib/hooks/useLibraryVerbs';
import { ownedByLabel } from '@lib/utils/libraryOwnership';
import { LibrarySectionId } from '@lib/utils/librarySections';
import { formatTimeAgoOrDate } from '@lib/utils/relativeTime';
import {
  formatBytesToString,
  formatDurationToString,
  formatMbToString,
  MB_TO_BYTES,
  pluralize,
} from '@lib/utils/units';
import { getLibraryUrl } from '@lib/utils/url';
import { LibraryStore } from '@stores/Library/Library';
import { libraryFavorites } from '@stores/LibraryFavorites';
import { useLibraryManager } from '@stores/LibraryManager/LibraryManager';
import { trackingStore } from '@stores/Tracking';
import classNames from 'classnames';
import { observer } from 'mobx-react';
import { RefObject, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
// Reuse the media table's dark styling so both list views share the same table chrome.
import gridCss from '@pages/Library/components/LibraryContentMediaTable/LibraryContentMediaTable.module.scss';
import { gridSortModelToLibrarySortHash, librarySortHashToGridModel } from '../utils/sortOptions';
import { useLibrariesOmniFilter } from './LibrariesOmniFilter';
import css from './LibrariesTable.module.scss';
import {
  LIBRARIES_COLUMN_ORDER,
  LIBRARIES_DEFAULT_VISIBILITY,
  LIBRARIES_LEADING_COLUMNS,
  LIBRARIES_TRAILING_COLUMNS,
} from './librariesTableColumns';
import { LibraryDeleteModalRef } from './LibraryDeleteModal';
import { LibraryMenuButton } from './LibraryMenuButton';
import { LibraryNameBadges } from './LibraryNameBadges';
import { buildLibraryRailItems } from './libraryRailItems';
import { LibraryRenameModalRef } from './LibraryRenameModal';
import { LibrarySectionLabel } from './LibrarySectionLabel';
import { ReadOnlyLibraryIndicator } from './ReadOnlyLibraryIndicator';

/**
 * Rows are a union because the table draws the same "Pinned" / "Other libraries" sections as
 * the card grid and the mobile list, and the table represents section headers as ordinary rows. A section is therefore a synthetic row whose first cell spans every
 * column.
 *
 * This is only safe because the grid runs `sortingMode="server"`: rows arrive pre-ordered and
 * the table never sorts a label into the middle of the data.
 */
type LibraryRow =
  | {
      /** DataGrid's default row id. */
      id: string;
      kind: 'library';
      library: LibraryStore;
    }
  | {
      /** Namespaced (`__section:<id>`) so it can never collide with a library id. */
      id: string;
      kind: 'section';
      sectionId: LibrarySectionId;
    };

/** Section rows are short; libraries keep the grid's default 64px. */
const SECTION_ROW_HEIGHT = 36;

/**
 * One named concept for "this row is a label, not a library", used by every grid callback that
 * would otherwise assume a row has a `library`. Five of them need it today (`colSpan`,
 * `getRowHeight`, `getRowClassName`, `onRowClick`, `handleCellKeyDown`) and any future grid prop
 * — `isRowSelectable`, checkbox selection, CSV export, row aria — has to make the same check.
 * Takes `unknown` so callbacks with broader row types can use the same guard.
 */
const isSectionRow = (row: unknown): row is Extract<LibraryRow, { kind: 'section' }> =>
  (row as LibraryRow | undefined)?.kind === 'section';

/** Stable row-height callback; section headers are shorter than library rows. */
const getLibraryRowHeight = ({ model }: GridRowHeightParams) =>
  isSectionRow(model) ? SECTION_ROW_HEIGHT : null;

/** Kills the hover fill and pointer cursor on section headers, so a label never looks clickable. */
const getLibraryRowClassName = ({ row }: GridRowClassNameParams<LibraryRow>) =>
  isSectionRow(row) ? css.sectionRow : '';

/**
 * Wraps a cell renderer so section rows render nothing, keeping the `kind` check in one place
 * instead of repeated through every column.
 *
 * Only the first column actually renders for a section row — `colSpan` covers the rest — but
 * the union makes `row.library` unavailable regardless, and one guard beats seven.
 *
 * Module-level on purpose: this must not become a dependency of the `columns` memo. See the
 * note on that memo for what a changed `columns` identity costs.
 */
const dataCell =
  (render: (library: LibraryStore) => React.ReactNode) =>
  ({ row }: GridRenderCellParams<LibraryRow>) =>
    isSectionRow(row) ? null : render(row.library);

/**
 * Cells are individually `observer`-wrapped rather than relying on the table's
 * own observer: The table renders cells in its own child components, so field reads
 * inside `renderCell` fall outside the table's reactive scope and a library's
 * stats landing later wouldn't repaint the row.
 */
const ThumbnailCell = observer(function ThumbnailCell({ library }: { library: LibraryStore }) {
  // Honour the user's chosen cover, as the gallery and Workspace do. Read the raw
  // `userMetadata` value rather than `library.thumbnailMediaId` — that getter falls
  // back to `latestMediaIds[0]`, which the hook would treat as an explicit choice
  // and so stop walking its fallback chain when that first candidate has no
  // thumbnail (common in audio-heavy libraries).
  const isDemo = !!library.isDemo;
  const { url, token, onError } = useLibraryThumbnail(library, {
    thumbnailMediaId: library.data?.userMetadata?.thumbnailMediaId,
    disabled: isDemo,
  });

  // The demo library gets its bundled cover, matching the gallery card. The card
  // plays the welcome video on hover; at 85×48 the poster frame is the useful part.
  if (isDemo) {
    return <img className={css.thumbnail} src={WelcomeLibraryThumbnail} alt={library.name || ''} />;
  }

  return url ? (
    <AuthenticatedImage
      className={css.thumbnail}
      src={url}
      token={token}
      alt={library.name || ''}
      onError={onError}
    />
  ) : (
    <div className={css.thumbnail}>
      <LibraryCardPlaceholder seed={library.libraryId} fit="contain" />
    </div>
  );
});

/**
 * Thumbnail and name share one cell, mirroring the media table's `filename`
 * column (`renderTitleWithThumbnail` → `TitleCell`), so the cover travels with the
 * name when the column is reordered or resized — and the whole thing sorts by name.
 */
const NameCell = observer(function NameCell({
  library,
  onAddMedia,
}: {
  library: LibraryStore;
  onAddMedia: (libraryId: string) => void;
}) {
  const { workWithAgent, findMoments } = useLibraryVerbs();
  const origin = { surface: 'libraries' as const, control: 'libraries-table-actions' };
  const libraryId = library.libraryId;
  // Only set for libraries somebody else owns — see `otherOwnerName`. Sits in the Name column
  // rather than getting a column of its own: it's absent on nearly every row for most users, so
  // a column would be a permanently empty one, and this belongs to the library's identity
  // anyway. `title` carries the full text, since the span ellipsises on a narrow column.
  const ownerName = library.otherOwnerName;

  return (
    <div className={css.nameCell}>
      <ThumbnailCell library={library} />
      <LibraryNameBadges library={library} />
      {ownerName && (
        <span className={css.ownerHint} title={ownedByLabel(ownerName)}>
          {ownedByLabel(ownerName)}
        </span>
      )}
      {/* Right-aligned inside the name column (`margin-left: auto` on `.verbRail`) and
          revealed on row hover, so a resting table reads as names and numbers rather
          than a wall of controls. Lives here rather than in its own column because
          right-aligned-within-Name keeps the actions on the same axis as the thing
          they act on, and the column flexes so they track the name's edge.
          The rail's buttons cancel their own clicks, so hitting one never triggers the
          row's navigate-to-library. */}
      <VerbRail
        className={css.verbRail}
        variant="inline"
        items={buildLibraryRailItems({
          isEmpty: library.stats?.count === 0,
          isReadOnly: library.isReadOnly,
          onWorkWithAgent: () => workWithAgent(libraryId, origin),
          onAddMedia: () => onAddMedia(libraryId),
          onFindMoments: () => findMoments(libraryId, origin),
          // Rails are stacked in a column and right-aligned, so an action that doesn't
          // apply to one library has to leave its space behind — otherwise that row's
          // glyphs slide sideways and nothing lines up vertically.
          keepLayout: true,
        })}
      />
    </div>
  );
});

/** Bare count — the "Total Media" header supplies the noun. */
const CountCell = observer(function CountCell({ library }: { library: LibraryStore }) {
  const count = library.stats?.count;
  return (
    <span className={css.stat}>{count === undefined ? '–' : count.toLocaleString('en-US')}</span>
  );
});

const DurationCell = observer(function DurationCell({ library }: { library: LibraryStore }) {
  const duration = library.stats?.mediaDuration;
  return (
    <span className={css.stat}>
      {duration === undefined
        ? '–'
        : // `detail: 2` spends the extra precision on a second unit rather than a
          // decimal — "282 h 13 min", not "282.22 h". It also handles "only when
          // applicable" for free: a duration that lands exactly on one unit has no
          // remainder to show, so 70 h stays "70 h".
          formatDurationToString(duration, { detail: 2 })}
    </span>
  );
});

/**
 * Subscribes to the pin store itself rather than receiving pin state from above, so that
 * state never has to travel as props.
 *
 * Reading favorites in each cell keeps the column definitions stable while pin toggles
 * update the affected glyphs. Widths and ordering remain owned by the table.
 */
const PinCell = observer(function PinCell({ library }: { library: LibraryStore }) {
  return (
    // Stops row-click navigation.
    <div className={css.actionCell} onClick={(e) => e.stopPropagation()}>
      {/* Pinning is a personal preference, not a write to the library, so read-only
          libraries stay pinnable — matching the card grid, which renders the pin
          outside the card for every library. */}
      <PinButton
        variant="inline"
        isFavorited={libraryFavorites.isFavorited(library.libraryId)}
        onToggle={() => libraryFavorites.toggleFavorite(library.libraryId, 'table')}
      />
    </div>
  );
});

const SizeCell = observer(function SizeCell({ library }: { library: LibraryStore }) {
  const size = library.stats?.mediaSize;
  // Library sizes are in MB, not bytes.
  return <span className={css.stat}>{size === undefined ? '–' : formatMbToString(size)}</span>;
});

/**
 * The search line's "160 libraries • 1,669h • 3TB", for the libraries on screen, worded like a
 * library's own stats line. An observer, so stats that land after the list repaint it.
 */
const LibrariesSummary = observer(function LibrariesSummary({
  libraries,
}: {
  libraries: LibraryStore[];
}) {
  const duration = libraries.reduce((sum, library) => sum + (library.stats?.mediaDuration ?? 0), 0);
  // Library sizes are in MB, not bytes.
  const size = libraries.reduce((sum, library) => sum + (library.stats?.mediaSize ?? 0), 0);
  return (
    <>
      {pluralize(libraries.length, 'library', 'libraries')}
      {duration > 0 && ` • ${formatDurationToString(duration, { spaceBeforeUnit: false })}`}
      {size > 0 && ` • ${formatBytesToString(size * MB_TO_BYTES, { spaceBeforeUnit: false })}`}
    </>
  );
});

const renderLibrariesSummary = (rows: LibraryRow[]) => (
  <LibrariesSummary libraries={rows.flatMap((row) => (isSectionRow(row) ? [] : [row.library]))} />
);

const DateCell = observer(function DateCell({
  library,
  field,
}: {
  library: LibraryStore;
  field: 'createdAt' | 'updatedAt';
}) {
  const timestamp = library.data?.[field];
  return <span className={css.stat}>{timestamp ? formatTimeAgoOrDate(timestamp) : '–'}</span>;
});

interface LibrariesTableProps {
  onLibrarySelect: (id: string, name: string) => void;
  renameModalRef: RefObject<LibraryRenameModalRef | null>;
  deleteModalRef: RefObject<LibraryDeleteModalRef | null>;
  /** Opens add-media for one library. Keep stable to avoid rebuilding column definitions. */
  onAddMedia: (libraryId: string) => void;
}

// Module-level so its identity is stable: an inline slot remounts on every render.
const ToolbarMatchAll = observer(() => {
  const { applied, matchAll, onMatchAllChange } = useLibrariesOmniFilter();
  return (
    <OmniMatchAllToggle query={applied} matchAll={matchAll} onMatchAllChange={onMatchAllChange} />
  );
});

/**
 * Desktop list view: a real data grid, mirroring the Library page's media table
 * so the two "list" modes share one interaction vocabulary — sortable column
 * headers, resizable and reorderable columns, hideable columns.
 *
 * Which columns are shown and the order they're in persist per account, through the same
 * `useGridColumnPreferences` hook the media table uses. Resized widths last until remount.
 *
 * Mobile keeps the hand-rolled row list (`LibraryList`), since a data grid is
 * unusable at 360px. That split matches the Library page's
 * `LibraryContentMediaTable` / `MobileMediaTableList` pair.
 *
 * Sorting is `server` mode: rows arrive pre-sorted from
 * `libraryManager.sortedAndFilteredLibraries` and header clicks write back to the
 * same store the sort dropdown uses, so the two controls cannot desync. That also means
 * pinned libraries arrive already hoisted to the top, exactly as on the other three surfaces.
 *
 * Server mode is likewise what makes the "Pinned" / "Other libraries" header rows safe: they
 * are synthetic rows in `rows`, and the table never re-sorts them into the middle of the data. They
 * also resolve what used to be a genuine contradiction here — a "Name ↑" arrow above rows that
 * were not in alphabetical order. Each section is now internally sorted, so the arrow is true
 * within the block it labels.
 */
export const LibrariesTable = observer(
  ({ onLibrarySelect, renameModalRef, deleteModalRef, onAddMedia }: LibrariesTableProps) => {
    const libraryManager = useLibraryManager();
    const navigate = useNavigate();
    // Needed by the column-preference hook to read the post-drag column order, and by the
    // section rows' `colSpan` to count the columns actually on screen.
    const apiRef = useGridApiRef();
    // The search line is the omnifilter while the table shows (the Filters pill steps aside).
    const omniFilter = useLibrariesOmniFilter();
    useTableFindShortcut(apiRef, omniFilter.onOpen);

    // A section header becomes a synthetic row ahead of its block. `buildLibrarySections`
    // decides whether there are any — shared with the card grid and the mobile list, so all
    // three group identically and no surface can invent a header the others don't draw.
    //
    // The store owns the grouping, so this only has to adapt it to the table row model. Read in the
    // **dependency array**, not just inside the factory: React evaluates deps every render, so
    // `observer` tracks the computed and an invalidation propagates as a fresh identity here. A
    // read living only inside the factory would be tracked solely in the renders where the
    // factory happens to run.
    const librarySections = libraryManager.librarySections;

    const rows = useMemo<LibraryRow[]>(
      () =>
        librarySections.flatMap((section) => [
          // The `__section:` prefix guarantees no collision with a library id; `kind` is what
          // actually discriminates the union.
          ...(section.id
            ? [{ id: `__section:${section.id}`, kind: 'section' as const, sectionId: section.id }]
            : []),
          ...section.items.map((library) => ({
            id: library.libraryId,
            kind: 'library' as const,
            library,
          })),
        ]),
      [librarySections],
    );

    const sortModel = useMemo(
      () => librarySortHashToGridModel(libraryManager.sortByHash),
      [libraryManager.sortByHash],
    );

    const openLibrary = useCallback(
      (library: LibraryStore) => navigate(getLibraryUrl(library.libraryId, library.name || '')),
      [navigate],
    );

    /**
     * DataGrid moves keyboard focus between *cells*, and Enter on a non-editable
     * cell does not fire `onRowClick` — so without this a keyboard user could arrow
     * through every library and never open one. Both sibling surfaces (the cards and
     * the mobile rows) are real links and were already reachable.
     *
     * Skipped for the pin and menu cells, whose own controls own the Enter key. The Name
     * cell now carries the verb rail too, but Enter there still opens the library —
     * those buttons are reached by Tab, and hijacking Enter on the row's identity cell
     * would cost the keyboard user the one thing the row is primarily for.
     *
     * Also skipped for section headers, which are rows only as far as the table is concerned and
     * have nothing to open.
     */
    const handleCellKeyDown = useCallback(
      (params: GridCellParams<LibraryRow>, event: React.KeyboardEvent) => {
        if (event.key !== 'Enter') return;
        if (isSectionRow(params.row)) return;
        if (params.field === 'pin' || params.field === 'menu') return;
        // A focused control inside the cell owns its own Enter. DataTable derives
        // `cellKeyDown` from the cell's `onKeyDown`, so a keypress on the Name cell's
        // verb rail bubbles here too — and `VerbRail` only cancels the *click*, not the
        // keydown. Without this, activating "Find moments" by keyboard would run the
        // search and then immediately have the grid navigate to the library over it.
        if ((event.target as HTMLElement | null)?.closest('button')) return;
        openLibrary(params.row.library);
      },
      [openLibrary],
    );

    const handleSortModelChange = useCallback(
      (model: GridSortModel) => {
        const hash = gridSortModelToLibrarySortHash(model);
        // A cleared model means "no sort", which this page has no representation
        // for — keep the previous sort rather than showing an arbitrary order.
        if (!hash || hash === libraryManager.sortByHash) return;
        libraryManager.setSorting(hash);
        trackingStore.librarySortChanged({ sortBy: hash });
      },
      [libraryManager],
    );

    const columns = useMemo<GridColDef<LibraryRow>[]>(
      () => {
        const cols: GridColDef<LibraryRow>[] = [
          {
            // Field names match `LibrarySortBy` so the sort bridge is a pass-through.
            field: 'title',
            headerName: 'Name',
            flex: 1,
            minWidth: 260,
            // The only left-aligned column — everything else is centred.
            align: 'left',
            headerAlign: 'left',
            hideable: false,
            disableReorder: true,
            renderCell: ({ row }) =>
              isSectionRow(row) ? null : <NameCell library={row.library} onAddMedia={onAddMedia} />,
          },
          {
            field: 'pin',
            disableReorder: true,
            colSpan: (_value, row, _column, api) =>
              isSectionRow(row) ? api.current.getVisibleColumns().length : undefined,
            headerName: '',
            width: 52,
            sortable: false,
            resizable: false,
            disableColumnMenu: true,
            hideable: false,
            align: 'center',
            renderCell: ({ row }) =>
              isSectionRow(row) ? (
                <LibrarySectionLabel id={row.sectionId} className={css.sectionLabel} />
              ) : (
                <PinCell library={row.library} />
              ),
          },
          {
            field: 'menu',
            disableReorder: true,
            headerName: '',
            width: 52,
            sortable: false,
            resizable: false,
            disableColumnMenu: true,
            hideable: false,
            align: 'center',
            renderCell: dataCell((library) => {
              if (library.isReadOnly) return <ReadOnlyLibraryIndicator />;
              return (
                <div className={css.actionCell} onClick={(e) => e.stopPropagation()}>
                  <LibraryMenuButton
                    onClick={() => onLibrarySelect(library.libraryId, library.name || '')}
                    disableDelete={!!(library.isDefault || library.isDemo)}
                    renameModalRef={renameModalRef}
                    deleteModalRef={deleteModalRef}
                    variant="row"
                    libraryId={library.libraryId}
                  />
                </div>
              );
            }),
          },
          // "Total" is dropped from these headers: a row *is* one library, so every
          // value is self-evidently that library's total, and repeating the prefix
          // across three of five columns is scaffolding rather than information.
          {
            field: 'count',
            headerName: 'Media',
            width: 120,
            align: 'center',
            headerAlign: 'center',
            renderCell: dataCell((library) => <CountCell library={library} />),
          },
          {
            field: 'duration',
            headerName: 'Duration',
            width: 130,
            align: 'center',
            headerAlign: 'center',
            renderCell: dataCell((library) => <DurationCell library={library} />),
          },
          {
            field: 'size',
            headerName: 'Size',
            width: 120,
            align: 'center',
            headerAlign: 'center',
            renderCell: dataCell((library) => <SizeCell library={library} />),
          },
          {
            field: 'added',
            headerName: 'Added',
            width: 150,
            align: 'center',
            headerAlign: 'center',
            renderCell: dataCell((library) => <DateCell library={library} field="createdAt" />),
          },
          {
            field: 'modified',
            headerName: 'Updated',
            width: 150,
            align: 'center',
            headerAlign: 'center',
            renderCell: dataCell((library) => <DateCell library={library} field="updatedAt" />),
          },
        ];

        return cols;
      },
      // Keep cell renderers and column definitions stable between data updates.
      [onLibrarySelect, renameModalRef, deleteModalRef, onAddMedia],
    );

    // Reuse the existing saved preference schema and fixed identity column.
    const columnPreferences = useGridColumnPreferences({
      tableId: 'libraries',
      apiRef,
      columns,
      defaultOrder: LIBRARIES_COLUMN_ORDER,
      defaultVisibility: LIBRARIES_DEFAULT_VISIBILITY,
      leadingFields: LIBRARIES_LEADING_COLUMNS,
      trailingFields: LIBRARIES_TRAILING_COLUMNS,
    });

    return (
      <div className={classNames(gridCss.dataGridContainer, css.container)}>
        <DataTable
          aria-label="Libraries"
          apiRef={apiRef}
          rows={rows}
          columns={columnPreferences.columns}
          columnVisibilityModel={columnPreferences.columnVisibilityModel}
          onColumnVisibilityModelChange={columnPreferences.onColumnVisibilityModelChange}
          onColumnOrderChange={columnPreferences.onColumnOrderChange}
          className={classNames(gridCss.dataGrid, css.grid)}
          rowHeight={64}
          getRowHeight={getLibraryRowHeight}
          getRowClassName={getLibraryRowClassName}
          columnHeaderHeight={44}
          // Two-state only: a third "unsorted" click would leave the store's sort
          // untouched while the header showed no arrow.
          sortingOrder={['asc', 'desc']}
          sortingMode="server"
          sortModel={sortModel}
          onSortModelChange={handleSortModelChange}
          onRowClick={({ row }) => {
            if (isSectionRow(row)) return;
            openLibrary(row.library);
          }}
          onCellKeyDown={handleCellKeyDown}
          disableRowSelectionOnClick
          disableColumnFilter
          searchField="title"
          searchValue={omniFilter.applied}
          onSearchChange={omniFilter.onChange}
          searchPlaceholder="Search name or library ID"
          searchEmptyText="No libraries match your search."
          renderSearchSummary={renderLibrariesSummary}
          isSectionRow={isSectionRow}
          slots={{ toolbarEnd: ToolbarMatchAll }}
        />
      </div>
    );
  },
);
